import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { I18nProvider, useI18n } from '@/i18n/I18nProvider'
import { DEFAULT_LOCALE, isLocale } from '@/i18n/locales'
import { HOME_URL, ROUTES, routeIn, type RouteDef } from './routes'
import Layout from './Layout'
import Seo from './Seo'
import ContentPage from '@/pages/ContentPage'
import NotFound from '@/pages/NotFound'

/**
 * Every written page renders through `ContentPage`, so there is no per-page
 * component map any more — the page *is* its sections. What used to be a
 * `Record<RouteDef['id'], ComponentType>` was a table whose only job was to map
 * six ids onto the same component.
 */
function Page({ route }: { route: RouteDef }) {
  const { locale, t } = useI18n()
  // A page written in fewer locales than the site serves (`RouteDef.locales`)
  // has no document in the others, so a client-side visit says so too.
  if (!routeIn(route, locale)) return <NotFound />
  return (
    <>
      <Seo route={route} />
      <ContentPage page={t.pages[route.id]} />
    </>
  )
}

/**
 * Everything under `/:locale`. An unrecognised first segment is a 404, not a
 * silent fallback to English: `/platfrom` should say it does not exist, and
 * serving English content from a URL that claims to be a locale would put a
 * wrong `hreflang` set on it.
 */
function LocaleShell() {
  const { locale } = useParams()

  if (!isLocale(locale)) {
    return (
      <I18nProvider locale={DEFAULT_LOCALE}>
        <NotFound />
      </I18nProvider>
    )
  }

  return (
    <I18nProvider locale={locale}>
      <Layout />
    </I18nProvider>
  )
}

export default function App() {
  return (
    <Routes>
      {/* The Worker normally negotiates `/` from `Accept-Language` and never
          lets this run. It is here for `vite dev`, where there is no Worker. */}
      <Route path="/" element={<Navigate to={HOME_URL} replace />} />
      <Route path="/:locale" element={<LocaleShell />}>
        {ROUTES.map((r) =>
          r.path === '' ? (
            <Route key={r.id} index element={<Page route={r} />} />
          ) : (
            <Route key={r.id} path={r.path} element={<Page route={r} />} />
          ),
        )}
      </Route>
    </Routes>
  )
}
